import { useState, useEffect, useMemo, useCallback } from "react";

export type UpdateCategory = "feature" | "improvement" | "fix" | "announcement";

export type DateFilter = "recent" | "this-month" | "this-year" | "all-time";

export interface UpdateMetadata {
    id: string;
    date: string;
    category: UpdateCategory;
    title: string;
    markdownFile: string;
    version?: string;
}

export interface Update extends UpdateMetadata {
    content?: string;
    isLoading?: boolean;
    error?: string;
}

interface UpdatesConfig {
    updates: UpdateMetadata[];
}

interface UseProductUpdatesOptions {
    categoryFilter?: UpdateCategory | null;
    dateFilter?: DateFilter;
    searchQuery?: string;
    /** Base URL for fetching updates. Defaults to "" (same origin) */
    baseUrl?: string;
}

export function useProductUpdates(options: UseProductUpdatesOptions = {}) {
    const { categoryFilter = null, dateFilter = "all-time", searchQuery = "", baseUrl = "" } = options;

    const [config, setConfig] = useState<UpdatesConfig | null>(null);
    const [updates, setUpdates] = useState<Update[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [loadedContent, setLoadedContent] = useState<Record<string, string>>({});

    // Fetch the updates.json config
    useEffect(() => {
        const fetchConfig = async () => {
            try {
                setIsLoading(true);
                const response = await fetch(`${baseUrl}/updates.json`);
                if (!response.ok) {
                    throw new Error("Failed to fetch updates config");
                }
                const data: UpdatesConfig = await response.json();
                setConfig(data);
                setUpdates(data.updates.map(meta => ({ ...meta })));
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to load updates");
            } finally {
                setIsLoading(false);
            }
        };

        fetchConfig();
    }, [baseUrl]);

    // Load markdown content for a specific update
    const loadContent = useCallback(async (updateId: string) => {
        // Find the update
        const update = updates.find(u => u.id === updateId);
        if (!update || loadedContent[updateId]) return;

        // Mark as loading
        setUpdates(prev =>
            prev.map(u => u.id === updateId ? { ...u, isLoading: true } : u)
        );

        try {
            const response = await fetch(`${baseUrl}/updates/${update.markdownFile}`);
            if (!response.ok) {
                throw new Error("Failed to fetch update content");
            }
            const content = await response.text();

            // Update the content cache
            setLoadedContent(prev => ({ ...prev, [updateId]: content }));

            // Update the updates list
            setUpdates(prev =>
                prev.map(u => u.id === updateId
                    ? { ...u, content, isLoading: false }
                    : u
                )
            );
        } catch (err) {
            setUpdates(prev =>
                prev.map(u => u.id === updateId
                    ? { ...u, isLoading: false, error: err instanceof Error ? err.message : "Failed to load content" }
                    : u
                )
            );
        }
    }, [updates, loadedContent, baseUrl]);

    // Apply filters
    const filteredUpdates = useMemo(() => {
        let result = [...updates];

        // Apply content from cache
        result = result.map(update => ({
            ...update,
            content: loadedContent[update.id] || update.content,
        }));

        // Category filter
        if (categoryFilter) {
            result = result.filter(update => update.category === categoryFilter);
        }

        // Search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            result = result.filter(update =>
                update.title.toLowerCase().includes(query)
            );
        }

        // Date filter
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const startOfYear = new Date(now.getFullYear(), 0, 1);
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

        switch (dateFilter) {
            case "recent":
                result = result.filter(update => new Date(update.date) >= thirtyDaysAgo);
                break;
            case "this-month":
                result = result.filter(update => new Date(update.date) >= startOfMonth);
                break;
            case "this-year":
                result = result.filter(update => new Date(update.date) >= startOfYear);
                break;
            case "all-time":
            default:
                // No filter
                break;
        }

        // Sort by date descending (newest first)
        result.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

        return result;
    }, [updates, categoryFilter, dateFilter, searchQuery, loadedContent]);

    // Get unique categories from updates
    const categories = useMemo(() => {
        const uniqueCategories = new Set(updates.map(u => u.category));
        return Array.from(uniqueCategories) as UpdateCategory[];
    }, [updates]);

    // Get category counts
    const categoryCounts = useMemo(() => {
        const counts: Record<UpdateCategory, number> = {
            feature: 0,
            improvement: 0,
            fix: 0,
            announcement: 0,
        };
        updates.forEach(update => {
            counts[update.category]++;
        });
        return counts;
    }, [updates]);

    return {
        updates: filteredUpdates,
        allUpdates: updates,
        isLoading,
        error,
        loadContent,
        categories,
        categoryCounts,
    };
}
